/**
 * Freshchat Helper - Business Logic
 *
 * This file contains all client-side logic for Freshchat integration.
 * Configuration is injected via window.__SHOPIFY_CUSTOMER__ and window.__FRESHCHAT_CONFIG__
 * from snippets/freshchat-helper.liquid
 */
(function () {
  var STORAGE_KEYS = {
    activeExternalId: "fc_active_external_id",
    restoreIdPrefix: "fc_restore_id_",
  };
  var EMPTY_RESTORE_ID_VALUE = "empty";

  var freshchatScriptLoadPromise = null;
  var postedRestoreIds = Object.create(null);
  var freshchatHashTriggerBound = false;

  // 读取由 Liquid snippet 注入的运行配置。
  function getConfig() {
    return window.__FRESHCHAT_CONFIG__ || {};
  }

  // 读取当前 Shopify 顾客对象，只接受已识别的登录用户。
  function getCurrentCustomer() {
    var customer = window.__SHOPIFY_CUSTOMER__;

    if (!customer || typeof customer !== "object") {
      return null;
    }

    if (!customer.email) {
      return null;
    }

    return customer;
  }

  // 统一整理顾客 email，作为稳定的身份键值使用。
  function getCurrentCustomerEmail() {
    var customer = getCurrentCustomer();

    if (!customer || !customer.email) {
      return null;
    }

    return String(customer.email).trim().toLowerCase() || null;
  }

  // 使用 email 作为 Freshchat 的 externalId。
  function getCurrentExternalId() {
    return getCurrentCustomerEmail();
  }

  // 生成每位顾客对应的 restoreId 本地存储 key。
  function getRestoreIdStorageKey(externalId) {
    if (!externalId) {
      return null;
    }

    return STORAGE_KEYS.restoreIdPrefix + encodeURIComponent(externalId);
  }

  // 读取这个浏览器里上次初始化 Freshchat 的顾客身份。
  function getActiveExternalId() {
    return localStorage.getItem(STORAGE_KEYS.activeExternalId);
  }

  // 记录当前活跃顾客，用来判断登录、登出或切换账号。
  function setActiveExternalId(externalId) {
    if (!externalId) {
      return;
    }

    localStorage.setItem(STORAGE_KEYS.activeExternalId, externalId);
  }

  // 回到游客状态时，清掉当前活跃顾客记录。
  function clearActiveExternalId() {
    localStorage.removeItem(STORAGE_KEYS.activeExternalId);
  }

  // 读取本地缓存的 restoreId，或约定值 "empty"。
  function getRestoreIdFromLocalStorage(externalId) {
    var storageKey = getRestoreIdStorageKey(externalId);

    if (!storageKey) {
      return null;
    }

    return localStorage.getItem(storageKey);
  }

  // 把真实 restoreId 或约定值 "empty" 保存到本地。
  function saveRestoreIdToLocalStorage(externalId, restoreId) {
    var storageKey = getRestoreIdStorageKey(externalId);

    if (!storageKey) {
      return;
    }

    localStorage.setItem(storageKey, restoreId);
  }

  // 删除某位顾客的本地 restoreId 状态。
  function removeRestoreIdFromLocalStorage(externalId) {
    var storageKey = getRestoreIdStorageKey(externalId);

    if (!storageKey) {
      return;
    }

    localStorage.removeItem(storageKey);
  }

  // 将字面值 "empty" 视为“后端确认目前没有 restoreId”。
  function isEmptyRestoreIdValue(restoreId) {
    return restoreId === EMPTY_RESTORE_ID_VALUE;
  }

  // 比较前后顾客身份，决定是否需要重置 Freshchat 上下文。
  function detectCustomerState() {
    var previousExternalId = getActiveExternalId();
    var currentExternalId = getCurrentExternalId();

    if (!previousExternalId && !currentExternalId) {
      return "guest";
    }

    if (!previousExternalId && currentExternalId) {
      return "logged_in";
    }

    if (previousExternalId && !currentExternalId) {
      return "logged_out";
    }

    if (previousExternalId === currentExternalId) {
      return "same_customer";
    }

    return "switched_customer";
  }

  // 向后端读取当前顾客已保存的 restoreId。
  function fetchRestoreId(externalId) {
    if (!externalId) {
      return Promise.resolve(null);
    }

    var config = getConfig();
    var endpoint =
      (config.appProxyRestoreIdEndpoint || "/apps/freshchat/restore-id") +
      `?userEmail=${encodeURIComponent(externalId)}&deviceType=pc`;

    return fetch(endpoint, {
      method: "GET",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
      },
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error("Failed to fetch Freshchat restoreId.");
        }

        return response.json();
      })
      .then(function (data) {
        if (!data || !data.data) {
          return null;
        }

        return data.data;
      })
      .catch(function (error) {
        console.warn("[Freshchat Helper] Could not fetch restoreId:", error);
        return null;
      });
  }

  // 把新拿到的 restoreId 回传给后端，供后续恢复对话使用。
  function updateRestoreId(externalId, restoreId) {
    if (!externalId || !restoreId) {
      return Promise.resolve(null);
    }

    var config = getConfig();
    var endpoint =
      (config.appProxyRestoreIdEndpoint || "/apps/freshchat/restore-id") +
      `?userEmail=${encodeURIComponent(externalId)}&deviceType=pc`;

    return fetch(endpoint, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        deviceType: "pc",
        chatId: restoreId,
      }),
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error("Failed to update Freshchat restoreId.");
        }

        return response.json();
      })
      .catch(function (error) {
        console.warn("[Freshchat Helper] Could not update restoreId:", error);
        return null;
      });
  }

  // 发现登出或切换账号时，清除 Freshchat 当前用户上下文。
  function clearFreshchatContext() {
    if (
      window.fcWidget &&
      window.fcWidget.user &&
      typeof window.fcWidget.user.clear === "function"
    ) {
      window.fcWidget.user.clear();
    }
  }

  // 让浏览器里的活跃顾客记录与当前登录状态保持一致。
  function updateActiveExternalIdState(externalId) {
    if (externalId) {
      setActiveExternalId(externalId);
      return;
    }

    clearActiveExternalId();
  }

  // 先读本地 restoreId，没有再问后端；如果后端为空则存成 "empty"。
  function getLocalOrRemoteRestoreId(externalId) {
    var localRestoreId = getRestoreIdFromLocalStorage(externalId);

    if (localRestoreId) {
      if (isEmptyRestoreIdValue(localRestoreId)) {
        return Promise.resolve(null);
      }

      return Promise.resolve(localRestoreId);
    }

    return fetchRestoreId(externalId).then(function (remoteRestoreId) {
      if (remoteRestoreId) {
        saveRestoreIdToLocalStorage(externalId, remoteRestoreId);
        return remoteRestoreId;
      }

      saveRestoreIdToLocalStorage(externalId, EMPTY_RESTORE_ID_VALUE);
      return Promise.resolve(null);
    });
  }

  // 组装 Freshchat 初始化数据；没有 restoreId 时只传 externalId。
  function setFreshchatMessengerConfig(externalId, restoreId) {
    if (!externalId) {
      delete window.fcWidgetMessengerConfig;
      return;
    }

    window.fcWidgetMessengerConfig = {
      externalId: externalId,
    };

    if (restoreId) {
      window.fcWidgetMessengerConfig.restoreId = restoreId;
    }
  }

  // 在同一个页面生命周期里只加载一次 Freshchat 脚本。
  function loadFreshchatScript() {
    if (freshchatScriptLoadPromise) {
      return freshchatScriptLoadPromise;
    }

    var config = getConfig();

    freshchatScriptLoadPromise = new Promise(function (resolve, reject) {
      var existingScript = document.querySelector(
        'script[data-freshchat-helper="true"]',
      );

      if (existingScript) {
        resolve(existingScript);
        return;
      }

      var script = document.createElement("script");
      script.src =
        config.freshchatScriptSrc || "//fw-cdn.com/12821437/5115946.js";
      script.async = true;
      script.setAttribute("chat", "true");
      script.setAttribute(
        "widgetId",
        config.freshchatWidgetId || "fc61cc43-4c7e-4806-8ee9-40c3f01fcd6e",
      );
      script.setAttribute("data-freshchat-helper", "true");
      script.onload = function () {
        resolve(script);
      };
      script.onerror = function () {
        reject(new Error("Failed to load Freshchat script."));
      };

      document.head.appendChild(script);
    }).catch(function (error) {
      console.warn(
        "[Freshchat Helper] Could not load Freshchat script:",
        error,
      );
      freshchatScriptLoadPromise = null;
      return null;
    });

    return freshchatScriptLoadPromise;
  }

  // 游客状态初始化 Freshchat，不带顾客身份。
  function initGuestFreshchat() {
    setFreshchatMessengerConfig(null, null);
    return loadFreshchatScript();
  }

  // 登录顾客初始化 Freshchat，按情况带上 restoreId。
  function initCustomerFreshchat(externalId, restoreId) {
    setFreshchatMessengerConfig(externalId, restoreId);
    return loadFreshchatScript();
  }

  // 等 Freshchat 用户接口可用后，从小组件里读取当前 restoreId。
  function readRestoreIdFromFreshchatUser(callback, attemptCount) {
    var attempts = attemptCount || 0;

    if (
      !window.fcWidget ||
      !window.fcWidget.user ||
      typeof window.fcWidget.user.get !== "function"
    ) {
      if (attempts < 20) {
        window.setTimeout(function () {
          readRestoreIdFromFreshchatUser(callback, attempts + 1);
        }, 500);
      }

      return;
    }

    window.fcWidget.user.get(function (response) {
      if (
        response &&
        response.status === 200 &&
        response.data &&
        response.data.restoreId
      ) {
        callback(response.data.restoreId);
      }
    });
  }

  // 发现新的 restoreId 时回传一次后端，并覆盖本地的 "empty"。
  function handleFreshchatRestoreIdCreated(externalId) {
    if (!externalId) {
      return;
    }

    readRestoreIdFromFreshchatUser(function (restoreId) {
      var postedRestoreIdKey = externalId + "::" + restoreId;
      var localRestoreId = getRestoreIdFromLocalStorage(externalId);

      if (
        localRestoreId === restoreId ||
        postedRestoreIds[postedRestoreIdKey]
      ) {
        return;
      }

      postedRestoreIds[postedRestoreIdKey] = true;

      updateRestoreId(externalId, restoreId).then(function (data) {
        if (data) {
          saveRestoreIdToLocalStorage(externalId, restoreId);
          return;
        }

        delete postedRestoreIds[postedRestoreIdKey];
      });
    });
  }

  // 监听小组件生命周期事件，方便初始化后补抓 restoreId。
  function bindFreshchatWidgetEvents(externalId) {
    window.fwcrm = window.fwcrm || function () {};
    window.fwcrm.on = window.fwcrm.on || function () {};

    window.fwcrm.on("widget:loaded", function () {
      handleFreshchatRestoreIdCreated(externalId);
    });

    window.fwcrm.on("user:created", function () {
      handleFreshchatRestoreIdCreated(externalId);
    });
  }

  // 当 URL 为 #open-chat 时打开 Freshchat；SDK 未就绪时等待 widget:loaded。
  function openFreshchatFromHash() {
    if (window.location.hash !== "#open-chat") {
      return;
    }

    if (
      window.fcWidget &&
      typeof window.fcWidget.open === "function" &&
      (typeof window.fcWidget.isLoaded !== "function" ||
        window.fcWidget.isLoaded())
    ) {
      window.fcWidget.open();
    }
  }

  // 关闭 Freshchat 后移除用于打开它的 URL hash，不刷新页面也不新增历史记录。
  function clearFreshchatHash() {
    if (window.location.hash !== "#open-chat") {
      return;
    }

    window.history.replaceState(
      window.history.state,
      document.title,
      window.location.pathname + window.location.search,
    );
  }

  // 注册 Freshchat URL hash trigger，同时兼容页面初始 hash 和后续 hashchange。
  function bindFreshchatHashTrigger() {
    if (freshchatHashTriggerBound) {
      return;
    }

    freshchatHashTriggerBound = true;
    window.addEventListener("hashchange", openFreshchatFromHash);

    window.fwcrm = window.fwcrm || function () {};
    window.fwcrm.on = window.fwcrm.on || function () {};
    window.fwcrm.on("widget:loaded", openFreshchatFromHash);
    window.fwcrm.on("widget:closed", clearFreshchatHash);

    openFreshchatFromHash();
  }

  // 主入口：处理旧上下文、读取 restoreId 状态，再启动小组件。
  function runFreshchatHelper() {
    bindFreshchatHashTrigger();
    var externalId = getCurrentExternalId();
    var customerState = detectCustomerState();

    if (
      customerState === "logged_out" ||
      customerState === "switched_customer"
    ) {
      clearFreshchatContext();
    }

    updateActiveExternalIdState(externalId);

    if (!externalId) {
      initGuestFreshchat();
      return;
    }

    getLocalOrRemoteRestoreId(externalId)
      .then(function (restoreId) {
        return initCustomerFreshchat(externalId, restoreId);
      })
      .then(function () {
        bindFreshchatWidgetEvents(externalId);
        handleFreshchatRestoreIdCreated(externalId);
      });
  }

  // 等页面完全加载后再启动，确保全局变量和 DOM 都已就绪。
  function runWhenPageLoaded() {
    if (document.readyState === "complete") {
      runFreshchatHelper();
      return;
    }

    window.addEventListener("load", runFreshchatHelper, { once: true });
  }

  runWhenPageLoaded();
})();
